// What the results card needs to talk to the arcade board: the game's
// identity on the service, the submission id a run is filed under, and the
// words for each outcome. Same shape as the slots and trivia helpers.
import type { SubmitResult } from "@/lib/leaderboards";
import { adjustedTimeMs, rawTimeMs } from "./scoring";
import type { Submission } from "./registration";

export const GAME = "sharp-mountain";
export const CLIENT = "sharp-mountain";
export const BOARD_SIZE = 10;
export const PRIVACY_URL = "https://arcade.coneyislandpottsville.com/privacy";
export const STORAGE_INITIALS = "sharp-mountain-initials";

// The id is minted when the results card mounts, before anything is posted:
// it is the outbox key and the value Turnstile binds the token to, so it has
// to exist before the widget renders. randomUUID is missing in some older
// WebViews; the fallback builds a version-4 id by hand.
export function mintSubmissionId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return (
    hex.slice(0, 8) +
    "-" +
    hex.slice(8, 12) +
    "-" +
    hex.slice(12, 16) +
    "-" +
    hex.slice(16, 20) +
    "-" +
    hex.slice(20)
  );
}

export function cleanInitials(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 3);
}

export function loadInitials(): string {
  try {
    return cleanInitials(localStorage.getItem(STORAGE_INITIALS) ?? "");
  } catch {
    return "";
  }
}

export function saveInitials(initials: string): void {
  try {
    localStorage.setItem(STORAGE_INITIALS, cleanInitials(initials));
  } catch {
    // a convenience only
  }
}

// A finished run in the shape the manifest wants. `time` is the ranked
// field, adjusted ms as seconds to three places; the service ranks on
// Math.round(time * 1000), so the two agree to the millisecond.
export function submissionFor(run: {
  finishSeconds: number;
  hotDogs: number;
  burgers: number;
  seed: number;
}): Submission {
  return {
    time: adjustedTimeMs(run) / 1000,
    rawTime: rawTimeMs(run.finishSeconds) / 1000,
    hotDogs: run.hotDogs,
    burgers: run.burgers,
    seed: run.seed,
  };
}

export function postedLine(result: SubmitResult): string {
  switch (result.status) {
    case "accepted":
      return `Posted as ${result.entry.initials}, #${result.rank} on the board.`;
    case "pending":
      return "Saved on this device. It posts the next time the game opens online.";
    case "rejected":
      return "The board turned this one down.";
    case "conflict":
      return "The board already has this run.";
  }
}
